import React, { useState, useEffect } from 'react';
import { apiCall, getUser } from '../lib/api.js';
import EvaluationDetail from '../components/EvaluationDetail.jsx';
import ScorecardForm from '../components/ScorecardForm.jsx';

const BAND_STYLES = {
  'Excellent': 'bg-emerald-50 text-emerald-700',
  'Good': 'bg-blue-50 text-blue-700',
  'Okay': 'bg-amber-50 text-amber-700',
  'Needs improvement': 'bg-orange-50 text-orange-700',
  'Failed': 'bg-red-50 text-red-700',
};

export default function EvaluationsPage({ initialSelected, onClearSelected }) {
  const [evals, setEvals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState('');
  const [agents, setAgents] = useState([]);
  const [evaluatorFilter, setEvaluatorFilter] = useState('all');
  const [evaluators, setEvaluators] = useState([]);
  const [selected, setSelected] = useState(initialSelected || null);
  const [editing, setEditing] = useState(null);
  const isAdmin = getUser()?.role === 'admin';

  const fetchEvals = async (agentName, evaluator = evaluatorFilter) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (agentName) qs.set('agent', agentName);
      if (evaluator && evaluator !== 'all') qs.set('evaluator', evaluator);
      const data = await apiCall('/evaluations?' + qs.toString());
      setEvals(data.evaluations || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    fetchEvals('');
    apiCall('/agents').then(d => setAgents(d.agents || [])).catch(() => {});
    if (isAdmin) {
      apiCall('/users').then(d => setEvaluators((d.users || []).filter(u => u.role !== 'agent'))).catch(() => {});
    }
  }, []);

  // Edit mode
  if (editing) {
    return <ScorecardForm editingEval={editing} onSaved={() => { setEditing(null); setSelected(null); fetchEvals(agent); }} onCancelEdit={() => setEditing(null)} />;
  }

  // Show detail view
  if (selected) {
    return <EvaluationDetail evaluation={selected} onBack={() => setSelected(null)} onEdit={(ev) => setEditing(ev)} />;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-lg font-medium">Evaluations</h2>
        <div className="flex flex-wrap items-center gap-2">
          {agents.map(a => (
            <button key={a.name} onClick={() => { setAgent(a.name); fetchEvals(a.name); }}
              className={'px-3 py-1.5 rounded-full text-xs font-medium border transition '
                + (agent === a.name ? 'bg-purple-50 text-purple-700 border-purple-200' : 'border-gray-200 text-gray-500 hover:border-gray-300')}>
              {a.name}
            </button>
          ))}
          <button onClick={() => { setAgent(''); fetchEvals(''); }}
            className={'px-3 py-1.5 rounded-full text-xs font-medium border transition '
              + (!agent ? 'bg-purple-50 text-purple-700 border-purple-200' : 'border-gray-200 text-gray-500 hover:border-gray-300')}>
            All
          </button>
          {/* Evaluator filter: admins only, to audit/track a specific scorer's work (e.g. manual scoring) */}
          {isAdmin && (
            <select value={evaluatorFilter}
              onChange={e => { setEvaluatorFilter(e.target.value); fetchEvals(agent, e.target.value); }}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs bg-white focus:ring-2 focus:ring-purple-500 outline-none">
              <option value="all">All evaluators</option>
              <option value="calibration">Calibration backfill (not counted)</option>
              {evaluators.map(u => <option key={u.email} value={u.email}>{u.email}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : evals.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No evaluations found</div>
      ) : (
        <div className="space-y-2">
          {evals.map(ev => (
            <button key={ev.id} onClick={() => setSelected(ev)}
              className="w-full text-left bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-4 hover:border-purple-200 hover:shadow-sm transition cursor-pointer">
              <div className={'text-lg font-medium w-14 text-center ' + (ev.autofail_triggered ? 'text-red-500' : 'text-gray-900')}>
                {Math.round((ev.final_score || 0) * 100)}%
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">#{ev.ticket_number} <span className="font-normal text-gray-500">{ev.ticket_subject}</span></div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {ev.eval_date} &middot; {ev.evaluator_email}
                  {ev.company && ev.company !== 'sabrent' && (
                    <span className="ml-1.5 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-medium capitalize">{ev.company}</span>
                  )}
                </div>
              </div>
              <span className="px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">{ev.agent_name}</span>
              <span className={'px-2.5 py-1 rounded-full text-xs font-medium ' + (BAND_STYLES[ev.rating_band] || 'bg-gray-100 text-gray-600')}>{ev.rating_band}</span>
              <i className="ti ti-chevron-right text-gray-300" aria-hidden="true"></i>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
